/**
 * Resolve an EpisodeOfCare's display name from its friendly-name identifier.
 *
 * The old planner code read `identifier[1].value || ___use` — a hardcoded array
 * index with a fall-through to the doc-level `___use`. That broke for the 18
 * auto-materialised EoCs created after 2026-06-10, when the "develop" business
 * identifier was dropped: their friendly name sits at `identifier[0]`, so index
 * 1 missed and the name fell through to `___use`, rendering
 * "EpisodeOfCare/AutoMaterialised/<name>" on the card (the string Ed reported).
 *
 * Fix: match the identifier by its `___use` tag ("friendly name", or the legacy
 * "generated friendly name" on e.g. Doll House) rather than by position, and
 * NEVER fall back to the doc-level `___use`. Robust to both identifier shapes.
 * Verified against prod: all 264 active EoCs resolve to a real name this way.
 */
export function friendlyEpisodeName(
  data: Record<string, unknown>,
  docId: string,
): string {
  const idents = Array.isArray(data.identifier)
    ? (data.identifier as Array<Record<string, unknown>>)
    : [];
  const friendly = idents.find((i) => {
    const use = i?.["___use"];
    const val = i?.value;
    return (
      typeof use === "string" &&
      use.toLowerCase().includes("friendly name") &&
      typeof val === "string" &&
      val.length > 0
    );
  });
  if (friendly) return friendly.value as string;
  // Deliberate final fallback: the doc id, NEVER the doc-level `___use`
  // (which is a developer annotation, not a name).
  return docId;
}
